import { StatusCodes } from 'http-status-codes';
import { USER_API_URL } from '../constants';
import {
  AuthError,
  AuthPayloadSuccess,
  AuthResponseSuccess,
  ERROR_MESSAGES,
  getAuthHeaders,
  handleAuthError,
  handleAuthSuccess
} from './authenticate';
import { logger } from './logger';

type PasswordlessRequestType =
  | 'email_authentication_request'
  | 'email_authentication_otp_verification'
  | 'email_authentication_email_resend'
  | 'email_authentication_magic_link_confirmation'
  | 'registration_completion';

type PasswordlessRequestBody = {
  data: {
    type: PasswordlessRequestType;
    attributes: Record<string, string | number | boolean | null | undefined>;
  };
};

type EmailAuthChallengeResponse = PasswordlessStatusResponse & {
  data?: {
    attributes?: {
      challenge_id?: string | null;
    };
  };
};

type PasswordlessStatusResponse = {
  status: string;
  message: string;
};

type PasswordlessInvalidResponse = {
  status: 'invalid_or_expired';
};

type PasswordlessErrorResponse = {
  errors: AuthError[] | [];
};

type RegistrationChallengeResponse = {
  status?: 'verified';
  data?: {
    attributes?: {
      email?: string;
      registration_token?: string;
    };
  };
  email?: string;
  meta?: {
    email?: string;
    registration_token?: string;
  };
  registration_token?: string;
};

export type PasswordlessRequestPayload = {
  email: string;
  bypassRackAttack?: string;
};

export type PasswordlessStartResult = {
  challengeId: string;
  message: string;
};

export type PasswordlessVerifyPayload = {
  challengeId: string;
  otp: string;
  cartId?: string | null;
  bypassRackAttack?: string;
};

export type PasswordlessResendPayload = {
  challengeId: string;
  bypassRackAttack?: string;
};

export type PasswordlessMagicLinkPayload = {
  challengeId: string;
  cartId?: string | null;
  uid: string;
  bypassRackAttack?: string;
};

export type RegistrationChallenge = {
  email: string;
  registrationToken: string;
};

export type EmailAuthResult =
  | {
      type: 'authenticated';
      user: AuthPayloadSuccess;
    }
  | {
      type: 'registration_required';
      registration: RegistrationChallenge;
    };

export type CompleteRegistrationPayload = {
  authorizationToken?: string | null;
  email: string;
  registrationToken: string;
  firstName: string;
  lastName: string;
  optedIn?: boolean;
  countryCode?: string;
  cartId?: string | null;
  genderPreference?: string;
  bypassRackAttack?: string;
};

export const PASSWORDLESS_ERROR_MESSAGES = {
  invalid_or_expired: 'The code or link is invalid or expired. Please try again.',
  rate_limited: ERROR_MESSAGES.recaptcha_failed,
  default: ERROR_MESSAGES.default
} as const;

const isPasswordlessStatusResponse = (
  response: PasswordlessStatusResponse | PasswordlessInvalidResponse | PasswordlessErrorResponse
): response is PasswordlessStatusResponse => 'status' in response && 'message' in response;

const isPasswordlessInvalidResponse = (
  response: PasswordlessStatusResponse | PasswordlessInvalidResponse | PasswordlessErrorResponse
): response is PasswordlessInvalidResponse =>
  'status' in response && response.status === 'invalid_or_expired';

const isPasswordlessErrorResponse = (
  response: PasswordlessStatusResponse | PasswordlessInvalidResponse | PasswordlessErrorResponse
): response is PasswordlessErrorResponse => 'errors' in response;

const isEmailAuthSuccessResponse = (response: unknown): response is AuthResponseSuccess =>
  typeof response === 'object' &&
  response != null &&
  'meta' in response &&
  'data' in response &&
  typeof (response as AuthResponseSuccess).data?.id === 'string';

const createPasswordlessError = (code: keyof typeof PASSWORDLESS_ERROR_MESSAGES): AuthError => ({
  code,
  message: PASSWORDLESS_ERROR_MESSAGES[code]
});

const getEmailAuthChallengeId = (response: EmailAuthChallengeResponse) => {
  const challengeId = response.data?.attributes?.challenge_id;

  if (typeof challengeId === 'string' && challengeId.length > 0) return challengeId;

  throw new Error(PASSWORDLESS_ERROR_MESSAGES.default);
};

const getRegistrationChallenge = (
  response: RegistrationChallengeResponse | PasswordlessInvalidResponse | PasswordlessErrorResponse,
  fallbackEmail?: string
): RegistrationChallenge => {
  const registrationResponse = response as RegistrationChallengeResponse;
  const registrationToken =
    registrationResponse.registration_token ||
    registrationResponse.meta?.registration_token ||
    registrationResponse.data?.attributes?.registration_token;
  const email =
    registrationResponse.email ||
    registrationResponse.meta?.email ||
    registrationResponse.data?.attributes?.email ||
    fallbackEmail;

  if (typeof registrationToken === 'string' && registrationToken.length > 0 && email) {
    return {
      email,
      registrationToken
    };
  }

  const passwordlessResponse = response as PasswordlessInvalidResponse | PasswordlessErrorResponse;

  if (
    isPasswordlessInvalidResponse(passwordlessResponse) ||
    isPasswordlessErrorResponse(passwordlessResponse)
  ) {
    handlePasswordlessStatusResponse(passwordlessResponse);
  }

  throw new Error(PASSWORDLESS_ERROR_MESSAGES.default);
};

const getEmailAuthResult = (
  response:
    | AuthResponseSuccess
    | RegistrationChallengeResponse
    | PasswordlessInvalidResponse
    | PasswordlessErrorResponse,
  fallbackEmail?: string
): EmailAuthResult => {
  if (isEmailAuthSuccessResponse(response)) {
    return {
      type: 'authenticated',
      user: handleAuthSuccess(response)
    };
  }

  return {
    type: 'registration_required',
    registration: getRegistrationChallenge(response, fallbackEmail)
  };
};

const postPasswordlessRequest = async <TResponse>(
  path: string,
  body: PasswordlessRequestBody,
  bypassRackAttack?: string,
  authorizationToken?: string | null
) => {
  const response = await fetch(`${USER_API_URL}${path}`, {
    method: 'POST',
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'same-origin',
    headers: getAuthHeaders(bypassRackAttack, authorizationToken),
    redirect: 'follow',
    referrer: 'no-referrer',
    body: JSON.stringify(body)
  });

  if (response.status === StatusCodes.TOO_MANY_REQUESTS) {
    throw createPasswordlessError('rate_limited');
  }

  return (await response.json().catch(() => {
    logger.error(
      'PASSWORDLESS_REQUEST_FAILED',
      `Passwordless request to ${path} failed with ${response.status}.`
    );
    throw createPasswordlessError('default');
  })) as TResponse;
};

const handlePasswordlessStatusResponse = (
  response: PasswordlessStatusResponse | PasswordlessInvalidResponse | PasswordlessErrorResponse
) => {
  if (isPasswordlessStatusResponse(response)) return response;

  if (isPasswordlessInvalidResponse(response)) {
    throw createPasswordlessError('invalid_or_expired');
  }

  if (isPasswordlessErrorResponse(response) && response.errors.length > 0) {
    return handleAuthError(response.errors[0]);
  }

  throw new Error(PASSWORDLESS_ERROR_MESSAGES.default);
};

export const requestPasswordlessLogin = async ({
  email,
  bypassRackAttack
}: PasswordlessRequestPayload): Promise<PasswordlessStartResult> => {
  const response = await postPasswordlessRequest<
    EmailAuthChallengeResponse | PasswordlessErrorResponse
  >(
    '/v2/email_auth/start',
    {
      data: {
        type: 'email_authentication_request',
        attributes: {
          email
        }
      }
    },
    bypassRackAttack
  );

  const startResponse = handlePasswordlessStatusResponse(response) as EmailAuthChallengeResponse;

  return {
    challengeId: getEmailAuthChallengeId(startResponse),
    message: startResponse.message
  };
};

export const resendPasswordlessEmail = async ({
  challengeId,
  bypassRackAttack
}: PasswordlessResendPayload) => {
  const response = await postPasswordlessRequest<
    PasswordlessStatusResponse | PasswordlessErrorResponse
  >(
    '/v2/email_auth/resend',
    {
      data: {
        type: 'email_authentication_email_resend',
        attributes: {
          challenge_id: challengeId
        }
      }
    },
    bypassRackAttack
  );

  return handlePasswordlessStatusResponse(response) as PasswordlessStatusResponse;
};

export const verifyEmailAuthOtp = async ({
  challengeId,
  otp,
  cartId,
  bypassRackAttack
}: PasswordlessVerifyPayload): Promise<EmailAuthResult> => {
  const response = await postPasswordlessRequest<
    | AuthResponseSuccess
    | RegistrationChallengeResponse
    | PasswordlessInvalidResponse
    | PasswordlessErrorResponse
  >(
    '/v2/email_auth/otp/verify',
    {
      data: {
        type: 'email_authentication_otp_verification',
        attributes: {
          challenge_id: challengeId,
          cart_id: cartId,
          otp
        }
      }
    },
    bypassRackAttack
  );

  return getEmailAuthResult(response);
};

export const confirmEmailAuthMagicLink = async ({
  challengeId,
  cartId,
  uid,
  bypassRackAttack
}: PasswordlessMagicLinkPayload): Promise<EmailAuthResult> => {
  const response = await postPasswordlessRequest<
    | AuthResponseSuccess
    | RegistrationChallengeResponse
    | PasswordlessInvalidResponse
    | PasswordlessErrorResponse
  >(
    '/v2/email_auth/magic_links/confirm',
    {
      data: {
        type: 'email_authentication_magic_link_confirmation',
        attributes: {
          challenge_id: challengeId,
          cart_id: cartId,
          uid
        }
      }
    },
    bypassRackAttack
  );

  return getEmailAuthResult(response);
};

export const completeEmailAuthRegistration = async ({
  authorizationToken,
  email,
  registrationToken,
  firstName,
  lastName,
  optedIn,
  countryCode,
  cartId,
  genderPreference,
  bypassRackAttack
}: CompleteRegistrationPayload) => {
  const response = await postPasswordlessRequest<AuthResponseSuccess | PasswordlessErrorResponse>(
    '/v2/registration/complete',
    {
      data: {
        type: 'registration_completion',
        attributes: {
          email,
          registration_token: registrationToken,
          first_name: firstName,
          last_name: lastName,
          opted_in: optedIn,
          country_code: countryCode,
          cart_id: cartId,
          gender_preference: genderPreference
        }
      }
    },
    bypassRackAttack,
    authorizationToken
  );

  if (isEmailAuthSuccessResponse(response)) return handleAuthSuccess(response);

  if (isPasswordlessErrorResponse(response) && response.errors.length > 0) {
    return handleAuthError(response.errors[0]);
  }

  throw new Error(PASSWORDLESS_ERROR_MESSAGES.default);
};
